/**
 * 詳情 CommandBar 分級 RWD：平鋪／workflow 外露＋「更多」門檻（viewport md／lg）。
 *
 * @typedef {{ key: string, label: string, ids: string[] }} ActionGroupDef
 */

/**
 * @param {Record<string, { available?: boolean }>} actionMap
 * @param {ActionGroupDef[]} actionGroups
 * @returns {string[]}
 */
export function getAvailableActionIdsInGroupOrder(actionMap, actionGroups) {
  return actionGroups.flatMap((group) => (
    group.ids.filter((actionId) => actionMap[actionId]?.available)
  ));
}

/**
 * @param {Record<string, { available?: boolean }>} actionMap
 * @param {string[]} workflowExposedOrder
 * @returns {string[]}
 */
export function pickAvailableWorkflowExposedIds(actionMap, workflowExposedOrder) {
  return workflowExposedOrder.filter((id) => actionMap[id]?.available);
}

/**
 * @param {Record<string, object>} actionMap
 * @param {ActionGroupDef[]} actionGroups
 * @param {{ includeEdit?: boolean, excludeIds?: string[] }} [options]
 */
export function collectMenuGroups(actionMap, actionGroups, options = {}) {
  const { includeEdit = true, excludeIds = [] } = options;
  const excluded = new Set(excludeIds);

  return actionGroups.map((group) => {
    const items = group.ids.reduce((acc, actionId) => {
      if (actionId === 'edit' && !includeEdit) {
        return acc;
      }
      const action = actionMap[actionId];
      if (!action?.available || excluded.has(actionId)) {
        return acc;
      }
      acc.push(action);
      return acc;
    }, []);
    return { key: group.key, label: group.label, items };
  }).filter((group) => group.items.length > 0);
}

/**
 * @param {ActionGroupDef[]} menuGroups
 */
function hasMenuItems(menuGroups) {
  return menuGroups.some((group) => group.items.length > 0);
}

/**
 * @param {object} params
 * @param {Record<string, object>} params.actionMap
 * @param {(map: Record<string, object>) => string[]} params.getExposedActionIds
 * @param {number} params.maxExposed
 */
function resolveExposedIdsForSplit({ actionMap, getExposedActionIds, maxExposed }) {
  const priorityIds = getExposedActionIds(actionMap).filter(
    (id) => actionMap[id]?.available,
  );
  return priorityIds.slice(0, maxExposed);
}

/**
 * @param {object} params
 * @param {Record<string, object>} params.actionMap
 * @param {ActionGroupDef[]} params.actionGroups
 * @param {(map: Record<string, object>) => string[]} params.getExposedActionIds
 * @param {number} params.flatThreshold
 * @param {number} params.maxExposed
 */
function resolveDesktopLayout({
  actionMap,
  actionGroups,
  getExposedActionIds,
  flatThreshold,
  maxExposed,
}) {
  const groupOrderIds = getAvailableActionIdsInGroupOrder(actionMap, actionGroups);
  const total = groupOrderIds.length;

  if (total < flatThreshold) {
    return {
      flatActions: groupOrderIds.map((id) => actionMap[id]),
      menuGroups: [],
      showMoreMenu: false,
    };
  }

  const exposedIds = resolveExposedIdsForSplit({
    actionMap,
    getExposedActionIds,
    maxExposed,
  });
  const menuGroups = collectMenuGroups(actionMap, actionGroups, {
    includeEdit: true,
    excludeIds: exposedIds,
  });

  return {
    flatActions: exposedIds.map((id) => actionMap[id]),
    menuGroups,
    showMoreMenu: hasMenuItems(menuGroups),
  };
}

/**
 * @param {Record<string, object>} actionMap
 * @param {ActionGroupDef[]} actionGroups
 */
export function resolveMobileMenuGroups(actionMap, actionGroups) {
  return collectMenuGroups(actionMap, actionGroups, {
    includeEdit: true,
    excludeIds: [],
  });
}

/**
 * 手機（< md）：N=1 直接 flatActions；N≥2 全收「操作」dropdown。
 *
 * @param {object} params
 * @param {Record<string, object>} params.actionMap
 * @param {ActionGroupDef[]} params.actionGroups
 */
export function resolveMobileLayout({ actionMap, actionGroups }) {
  const groupOrderIds = getAvailableActionIdsInGroupOrder(actionMap, actionGroups);
  const total = groupOrderIds.length;

  if (total <= 1) {
    return {
      flatActions: groupOrderIds.map((id) => actionMap[id]),
      menuGroups: [],
      showMoreMenu: false,
    };
  }

  const menuGroups = resolveMobileMenuGroups(actionMap, actionGroups);

  return {
    flatActions: [],
    menuGroups,
    showMoreMenu: hasMenuItems(menuGroups),
  };
}

/**
 * md（768～1023）：N&lt;2 全平鋪；N≥2 → 最多 1 workflow 外露 +「更多」
 *
 * @param {object} params
 * @param {Record<string, object>} params.actionMap
 * @param {ActionGroupDef[]} params.actionGroups
 * @param {(map: Record<string, object>) => string[]} params.getExposedActionIds
 */
export function resolveMdDesktopLayout({ actionMap, actionGroups, getExposedActionIds }) {
  return resolveDesktopLayout({
    actionMap,
    actionGroups,
    getExposedActionIds,
    flatThreshold: 2,
    maxExposed: 1,
  });
}

/**
 * lg（≥1024）：N&lt;3 全平鋪；N≥3 → 最多 2 workflow 外露 +「更多」
 *
 * @param {object} params
 * @param {Record<string, object>} params.actionMap
 * @param {ActionGroupDef[]} params.actionGroups
 * @param {(map: Record<string, object>) => string[]} params.getExposedActionIds
 */
export function resolveLgDesktopLayout({ actionMap, actionGroups, getExposedActionIds }) {
  return resolveDesktopLayout({
    actionMap,
    actionGroups,
    getExposedActionIds,
    flatThreshold: 3,
    maxExposed: 2,
  });
}
